import React from 'react';

// Time-range pill row for historical charts — same affordance as the Price
// widget's selector (visual style mirrored from PriceWidgetRenderer).
// `rows` slices by trailing row count (daily/quarterly/annual series);
// `months` slices by calendar cutoff (for irregular cadences like bi-monthly
// short-interest settlements, where row count ≠ elapsed time).
export interface RangeOption {
  value: string;
  label: string;
  rows?: number;
  months?: number;
}

/** Trailing-N slice for evenly-spaced series. No-op when n is undefined. */
export function sliceByRows<T>(data: T[], n?: number): T[] {
  if (!n || n <= 0 || data.length <= n) return data;
  return data.slice(-n);
}

/**
 * Calendar-cutoff slice, anchored to the newest date IN the data (not "now",
 * so lagging datasets don't under-fill). No-op when months is undefined.
 */
export function sliceByMonths<T extends object>(
  data: T[],
  dateKey: keyof T,
  months?: number,
): T[] {
  if (!months || months <= 0 || data.length === 0) return data;
  const times = data
    .map(row => new Date(String(row[dateKey])).getTime())
    .filter(t => !Number.isNaN(t));
  if (!times.length) return data;
  const anchor = new Date(Math.max(...times));
  const cutoff = new Date(anchor);
  cutoff.setMonth(cutoff.getMonth() - months);
  const cutoffTime = cutoff.getTime();
  return data.filter(row => {
    const t = new Date(String(row[dateKey])).getTime();
    return Number.isNaN(t) ? true : t >= cutoffTime;
  });
}

/**
 * Values to disable so pills never lie: a sized option is redundant when it
 * already shows the entire dataset and a smaller option (or an explicit
 * "all" option) does too. The smallest all-covering sized option stays
 * enabled unless an unsized ("All") option exists to play that role.
 */
export function redundantRangeValues<T extends object>(
  options: RangeOption[],
  data: T[],
  dateKey?: keyof T,
): Set<string> {
  const disabled = new Set<string>();
  if (data.length === 0) return disabled;
  const hasAllOption = options.some(o => o.rows == null && o.months == null);

  const visibleCount = (o: RangeOption): number => {
    if (o.rows != null) return sliceByRows(data, o.rows).length;
    if (o.months != null) {
      // Without dateKey we can't compute the actual visible count — treat as
      // non-covering so the pill is never incorrectly marked redundant.
      return dateKey ? sliceByMonths(data, dateKey, o.months).length : 0;
    }
    return data.length; // unsized "All" option
  };

  const sized = options
    .filter(o => o.rows != null || o.months != null)
    .map(o => ({ option: o, size: o.rows ?? o.months ?? 0, covers: visibleCount(o) === data.length }))
    .sort((a, b) => a.size - b.size);

  let keptOneCovering = hasAllOption; // "All" already represents the full set
  for (const { option, covers } of sized) {
    if (!covers) continue;
    if (keptOneCovering) disabled.add(option.value);
    keptOneCovering = true;
  }
  return disabled;
}

interface ChartRangeSelectorProps {
  options: RangeOption[];
  value: string;
  onChange: (value: string) => void;
  /** Values rendered disabled (from redundantRangeValues). */
  disabledValues?: Set<string>;
  /** Flat low-height pills for dense single-line toolbars (e.g. Correlation). */
  compact?: boolean;
  className?: string;
}

const ChartRangeSelector: React.FC<ChartRangeSelectorProps> = ({
  options,
  value,
  onChange,
  disabledValues,
  compact = false,
  className = '',
}) => (
  <div
    className={
      compact
        ? `flex max-w-full flex-wrap items-center gap-0.5 ${className}`
        : `flex max-w-full flex-wrap items-center justify-end gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1 ${className}`
    }
  >
    {options.map(option => {
      const isActive = value === option.value;
      const isDisabled = !isActive && (disabledValues?.has(option.value) ?? false);
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => !isDisabled && onChange(option.value)}
          disabled={isDisabled}
          title={isDisabled ? 'Entire available history already shown' : undefined}
          className={`${compact ? 'rounded h-[22px] px-1.5 text-[10px]' : 'rounded-lg px-2.5 py-1 text-[11px]'} font-semibold transition-colors ${
            isActive
              ? 'bg-primary-600 text-white shadow-sm'
              : isDisabled
                ? 'text-neutral-300 cursor-not-allowed'
                : 'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export default ChartRangeSelector;
